import React, { useEffect, useState } from "react";
import DataTable from "../DataTable";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import { gql, useMutation, useQuery } from "@apollo/client";
import Loader from "../../Loader";
import Switch from "react-switch";
import { useAuthz } from "../../auth/useAuthz";

const GET_PREREQUISITE_CHECKLIST_ITEMS = gql`
  query GetAdminPrerequisiteChecklistItems {
    getAdminPrerequisiteChecklistItems {
      id
      title
      description
      isActive
      services {
        id
        name
      }
    }
  }
`;

const GET_SERVICES_FOR_PICKER = gql`
  query GetAdminServiceForPrerequisitePicker($data: Filter!) {
    getAdminService(data: $data) {
      services {
        id
        name
      }
    }
  }
`;

const ADD_PREREQUISITE_CHECKLIST_ITEM = gql`
  mutation AddPrerequisiteChecklistItem(
    $data: AddPrerequisiteChecklistItemInput!
  ) {
    addPrerequisiteChecklistItem(data: $data) {
      status
      message
    }
  }
`;

const UPDATE_PREREQUISITE_CHECKLIST_ITEM = gql`
  mutation UpdatePrerequisiteChecklistItem(
    $data: UpdatePrerequisiteChecklistItemInput!
  ) {
    updatePrerequisiteChecklistItem(data: $data) {
      status
      message
    }
  }
`;

const emptyForm = { id: null, title: "", description: "", serviceIds: [] };

const PrerequisiteChecklistForm = ({ initial, services, onCancel, onSaved }) => {
  const isEdit = Boolean(initial.id);
  const [title, setTitle] = useState(initial.title);
  const [description, setDescription] = useState(initial.description || "");
  const [serviceIds, setServiceIds] = useState(initial.serviceIds);

  const [addItem, { loading: addLoading }] = useMutation(
    ADD_PREREQUISITE_CHECKLIST_ITEM,
  );
  const [updateItem, { loading: updateLoading }] = useMutation(
    UPDATE_PREREQUISITE_CHECKLIST_ITEM,
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const mutate = isEdit
      ? updateItem({ variables: { data: { id: initial.id, title, description, serviceIds } } })
      : addItem({ variables: { data: { title, description, serviceIds } } });

    mutate.then((res) => {
      const result = isEdit
        ? res.data.updatePrerequisiteChecklistItem
        : res.data.addPrerequisiteChecklistItem;
      if (result.status === "SUCCESS") {
        onSaved();
      }
    });
  };

  return (
    <div className="flex flex-col py-3 px-2 space-y-5">
      <h3 className="text-[#FBB033] font-be-jost-pro font-bold text-2xl">
        {isEdit ? "Edit Prerequisite Checklist Item" : "Add Prerequisite Checklist Item"}
      </h3>
      <form className="w-full flex flex-col space-y-8" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Title*"
          className="px-2 py-1 bg-[#0c0b0b] border-b-[1px] border-b-[#E0E0E0] outline-none text-white"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <input
          type="text"
          placeholder="Description"
          className="px-2 py-1 bg-[#0c0b0b] border-b-[1px] border-b-[#E0E0E0] outline-none text-white"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="flex flex-col space-y-2">
          <p className="text-[#9A9A9A] font-be-jost-pro text-sm">
            Services this checklist item applies to
          </p>
          <div className="flex flex-col space-y-1 max-h-80 overflow-y-auto">
            {services.map((service) => {
              const checked = serviceIds.includes(service.id);
              return (
                <label
                  key={service.id}
                  className="flex items-center gap-2 text-white text-sm"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      setServiceIds((prev) =>
                        e.target.checked
                          ? [...prev, service.id]
                          : prev.filter((id) => id !== service.id),
                      )
                    }
                  />
                  {service.name}
                </label>
              );
            })}
          </div>
        </div>
        <div className="flex items-center justify-center space-x-3">
          <button
            type="submit"
            disabled={addLoading || updateLoading || !title}
            className="bg-[#FBB034] text-[#202125] py-2 px-6 rounded-md font-poppins text-sm font-bold disabled:opacity-50"
          >
            {isEdit ? "Save Changes" : "Add Item"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="border border-[#FBB033] text-[#FBB033] py-2 px-6 rounded-md font-poppins text-sm font-bold"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const PrerequisiteChecklistTable = () => {
  const { can } = useAuthz();
  const { data, loading, error, refetch } = useQuery(
    GET_PREREQUISITE_CHECKLIST_ITEMS,
  );
  const { data: servicesData, loading: servicesLoading } = useQuery(
    GET_SERVICES_FOR_PICKER,
    { variables: { data: { isActive: null, order: "", search: "" } } },
  );
  const [updateItem] = useMutation(UPDATE_PREREQUISITE_CHECKLIST_ITEM);

  const [mode, setMode] = useState("list"); // list | add | edit
  const [editTarget, setEditTarget] = useState(emptyForm);

  const services = servicesData?.getAdminService?.services || [];
  const items = data?.getAdminPrerequisiteChecklistItems || [];

  const handleSaved = () => {
    setMode("list");
    refetch();
  };

  if (loading || servicesLoading) return <Loader />;

  if (mode !== "list") {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <Layout>
          <PrerequisiteChecklistForm
            initial={mode === "edit" ? editTarget : emptyForm}
            services={services}
            onCancel={() => setMode("list")}
            onSaved={handleSaved}
          />
        </Layout>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="flex flex-col space-y-6 pt-8">
          <div className="flex justify-between items-center px-5">
            <h3 className="text-[#FBB033] font-be-jost-pro font-bold text-2xl">
              Prerequisite Checklist Items
            </h3>
            {can("services.create") && (
              <button
                className="font-be-jost-pro font-medium text-sm border border-[#FBB033] text-[#FBB033] rounded-md px-5 py-2"
                onClick={() => setMode("add")}
              >
                Add Item
              </button>
            )}
          </div>
          <div>
            <DataTable tableName="PrerequisiteChecklistItems">
              <thead>
                <tr>
                  <th data-priority="1" className="text-white">
                    Title
                  </th>
                  <th data-priority="2" className="text-white">
                    Services
                  </th>
                  <th data-priority="3" className="text-white">
                    isActive
                  </th>
                  <th data-priority="4" className="text-white">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b-2 border-b-[#E0E0E0]">
                    <td className="text-white w-[20%] px-1 border-b-[1px] border-b-[#E0E0E0]">
                      {row.title}
                    </td>
                    <td className="text-white w-[40%] px-1 border-b-[1px] border-b-[#E0E0E0]">
                      {row.services.map((s) => s.name).join(", ")}
                    </td>
                    <td className="text-white w-[10%] px-1 border-b-[1px] border-b-[#E0E0E0]">
                      <Switch
                        checked={row.isActive}
                        onChange={() =>
                          updateItem({
                            variables: {
                              data: { id: row.id, isActive: !row.isActive },
                            },
                          }).then((res) => {
                            if (
                              res.data.updatePrerequisiteChecklistItem
                                .status === "SUCCESS"
                            ) {
                              refetch();
                            }
                          })
                        }
                        onColor="#86d3ff"
                        onHandleColor="#2693e6"
                        handleDiameter={30}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        offColor="#ffffff"
                        offHandleColor="#fbb033"
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={20}
                        width={48}
                        className="react-switch"
                      />
                    </td>
                    <td className="w-[10%] text-center px-2 border-b-[1px] border-b-[#E0E0E0]">
                      {can("services.edit") && (
                        <button
                          className="rounded-md bg-white py-2 px-3 text-black text-sm font-medium"
                          onClick={() => {
                            setEditTarget({
                              id: row.id,
                              title: row.title,
                              description: row.description || "",
                              serviceIds: row.services.map((s) => s.id),
                            });
                            setMode("edit");
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default PrerequisiteChecklistTable;
